// AdminUserDetail — full user record + edit form + recent deals.
import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft } from "lucide-react";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users/${id}`);
    setData(data);
    setForm({ ...data.user });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    setSaving(true);
    try {
      const patch = {
        name: form.name, subscription_plan: form.subscription_plan,
        language: form.language, default_country: form.default_country,
        display_currency: form.display_currency, unit_system: form.unit_system,
        is_admin: !!form.is_admin, is_suspended: !!form.is_suspended,
      };
      await api.patch(`/admin/users/${id}`, patch);
      toast.success("User updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading user…</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="admin-user-detail">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/users" className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3 w-3" /> All users</Link>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">{data.user.email}</h1>
          <p className="mt-1 text-xs text-slate-500">id: {data.user.id} · created {data.user.created_at?.slice(0, 10)}</p>
        </div>
        <Button onClick={onSave} disabled={saving} className="bg-indigo-600 text-white hover:bg-indigo-700">
          {saving ? <Loader2 className="me-1 h-3 w-3 animate-spin" /> : null} Save changes
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">Profile</h2>
          <div className="mt-4 grid gap-4">
            <div><Label>Name</Label><Input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Plan</Label>
              <Select value={form.subscription_plan} onValueChange={(v) => setForm({ ...form, subscription_plan: v })}>
                <SelectTrigger data-testid="edit-plan" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="pro">Pro</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Language</Label><Input data-testid="edit-lang" value={form.language || "en"} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-1" /></div>
              <div><Label>Currency</Label><Input data-testid="edit-currency" value={form.display_currency || "USD"} onChange={(e) => setForm({ ...form, display_currency: e.target.value.toUpperCase() })} className="mt-1" /></div>
              <div><Label>Country</Label><Input data-testid="edit-country" value={form.default_country || "US"} onChange={(e) => setForm({ ...form, default_country: e.target.value.toUpperCase() })} className="mt-1" /></div>
            </div>
            <div><Label>Unit system</Label>
              <Select value={form.unit_system || "imperial"} onValueChange={(v) => setForm({ ...form, unit_system: v })}>
                <SelectTrigger data-testid="edit-units" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="imperial">Imperial (ft²)</SelectItem><SelectItem value="metric">Metric (m²)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">Access flags</h2>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between"><Label>Admin</Label><Switch checked={!!form.is_admin} onCheckedChange={(v) => setForm({ ...form, is_admin: v })} data-testid="toggle-admin" /></div>
              <div className="flex items-center justify-between"><Label>Suspended</Label><Switch checked={!!form.is_suspended} onCheckedChange={(v) => setForm({ ...form, is_suspended: v })} data-testid="toggle-suspend" /></div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">Activity</h2>
            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Deals</dt><dd className="font-medium tabular-nums">{data.deals.length}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Conversations</dt><dd className="font-medium tabular-nums">{data.conversation_count}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Last AI call</dt><dd className="font-mono text-xs">{data.last_ai_call?.created_at?.slice(0, 16) || "—"}</dd></div>
            </dl>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">Recent deals</h2>
        {data.deals.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No deals.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200">
            {data.deals.slice(0, 10).map((d) => (
              <li key={d.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">{d.country} · score {d.deal_score} · {d.verdict}</span>
                <Link to={`/admin/deals`} className="text-xs text-indigo-600 hover:underline">id {d.id.slice(0, 8)}…</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
